import {
  UsergroupAddOutlined,
  LinkOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { Modal, Input, Typography, Select, Divider, Form } from 'antd';
import { useEffect, useState } from 'react';
import { Button } from '../../baseComponents';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { FronteggContext } from '@frontegg/rest-api';
import axios from 'axios';
import moment from 'moment';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';

const getPublicRoles = (fronteggRoles) => {
  const publicRoles = ['admin', 'user', 'clinician', 'medical-assistant'];
  return (fronteggRoles || [])?.filter((role) =>
    publicRoles.includes((role?.key || '').toLowerCase().trim())
  );
};

const inviteUsersToFronteggTenant = async ({ user }) => {
  const token = FronteggContext.getAccessToken();
  const tenantId = FronteggContext.getUser().tenantId;
  return await axios.post(
    `${
      import.meta.env.VITE_FRONTEGG_BASE_URL
    }/frontegg/identity/resources/users/v2`,

    user,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'frontegg-tenant-id': tenantId,
      },
    }
  );
};

const fetchTenantRoles = async () => {
  const token = FronteggContext.getAccessToken();
  const response = await axios.get(
    `${
      import.meta.env.VITE_FRONTEGG_BASE_URL
    }/frontegg/team/resources/roles/v1`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (response.status === 200) {
    return getPublicRoles(response.data.items ?? []);
  }
  return [];
};

const fetchUserInviteSettings = async () => {
  const token = FronteggContext.getAccessToken();
  const response = await axios.get(
    `${
      import.meta.env.VITE_FRONTEGG_BASE_URL
    }/frontegg/identity/resources/tenants/invites/v1/user`,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (response.status === 200) {
    return response.data;
  }
  return null;
};

export function InviteUser({ mobile, isMinimized }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [inviteSettings, setInviteSettings] = useState(null);
  const { user } = useAuth();
  const [form] = Form.useForm();

  const [roleState, setRoleState] = useState({ roles: [], isLoading: true });

  const openModal = () => {
    analytics.track('Clicked Invite Users', { mobile, email: user?.email });
    setIsModalOpen(true);
  };

  const cancelModal = () => {
    analytics.track('Clicked Cancel In Invite Users', {
      mobile,
      email: user?.email,
    });
    setIsModalOpen(false);
  };

  useEffect(() => {
    fetchTenantRoles()
      .then((roles) => {
        setRoleState({
          roles: roles.map((role) => ({
            label: role.name,
            value: role.id,
            description: role.description,
          })),
          isLoading: false,
        });
      })
      .catch(() => {
        setRoleState({ roles: [], isLoading: false });
      });

    fetchUserInviteSettings().then((settings) => {
      setInviteSettings(settings);
    });
  }, []);

  const copyInviteLink = () => {
    analytics.track('Clicked Copy Invite Link', { mobile, email: user?.email });
    const link = `${
      import.meta.env.VITE_FRONTEGG_BASE_URL
    }/oauth/prelogin?redirectUrl=${
      import.meta.env.VITE_AMBIENT_UI_BASE_URL
    }&invitationToken=${inviteSettings.token}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setInviteLinkCopied(true);
        return new Promise((resolve) => {
          setTimeout(resolve, 3000);
        });
      })
      .then(() => {
        setInviteLinkCopied(false);
      });
  };

  const inviteUser = async () => {
    const { email, role, firstName, lastName } = await form.validateFields();

    try {
      setIsSubmitting(true);
      analytics.track('Created Submit In Invite User', {
        firstName,
        lastName,
        email,
        mobile,
      });
      await inviteUsersToFronteggTenant({
        user: {
          name: [firstName, lastName].filter(Boolean).join(' '),
          email,
          roleIds: role,
          skipInviteEmail: false,
        },
      });
      setIsModalOpen(false);
      showToastSuccess('User invited successfully');
    } catch (error) {
      showToastError(
        error?.response?.data?.errors?.[0] || 'Failed to invite user'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  function renderInviteModal() {
    return (
      <Modal
        maskClosable={false}
        closable={false}
        title="Invite User"
        open
        onCancel={cancelModal}
        footer={
          <div className="flex justify-end gap-2 mt-8">
            <Button
              disabled={isSubmitting}
              className="ih-button-secondary"
              onClick={cancelModal}
            >
              Cancel
            </Button>
            <Button
              isLoading={isSubmitting}
              type="submit"
              className="ih-button-primary"
              onClick={inviteUser}
            >
              Invite
            </Button>
          </div>
        }
      >
        <div className="flex flex-col">
          <Form autoComplete="off" layout="vertical" form={form}>
            <Form.Item
              name="firstName"
              label="First Name"
              className="field-wrapper"
              rules={[{ required: true, message: 'First Name is required' }]}
            >
              <Input
                className="field"
                type="text"
                placeholder="Enter first name"
              />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Last Name"
              className="field-wrapper"
              rules={[{ required: true, message: 'Last Name is required' }]}
            >
              <Input
                className="field"
                type="text"
                placeholder="Enter last name"
              />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              className="field-wrapper"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Enter a valid email' },
              ]}
            >
              <Input className="field" type="email" placeholder="Enter email" />
            </Form.Item>
            <Form.Item
              name="role"
              label="Role"
              className="field-wrapper"
              rules={[{ required: true, message: 'Role is required' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select Role"
                className="field"
                options={roleState.roles}
                optionRender={({ label, data }) => (
                  <div className="flex flex-col">
                    <span>{label}</span>
                    <span className="text-gray-500 text-xs whitespace-normal">
                      {data.description}
                    </span>
                  </div>
                )}
                disabled={!roleState.roles?.length}
                loading={roleState.isLoading}
              />
            </Form.Item>
          </Form>
          {inviteSettings && (
            <>
              <Divider />
              <div className={`flex flex-col gap-1 items-start`}>
                <Typography.Text strong>Invite with link</Typography.Text>
                <Typography.Text className="text-gray-500 text-sm mb-2">
                  Link will expire in{' '}
                  {moment(inviteSettings.expires).diff(moment(), 'days')} days
                </Typography.Text>
                <Button onClick={copyInviteLink} className="ih-button-primary">
                  <span className="inline-flex items-center justify-center gap-2 h-8">
                    {inviteLinkCopied ? (
                      <>
                        <CheckOutlined />
                        Copied
                      </>
                    ) : (
                      <>
                        <LinkOutlined />
                        Copy invite link
                      </>
                    )}
                  </span>
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  }

  const lgButton = (
    <button
      className={`w-full text-white bg-gradient-to-br from-pink-500 to-blue-700 bg-origin-border bg-clip-padding flex justify-center items-center p-2 rounded-xl hover:shadow-xl font-medium px-4 gap-2`}
      type="button"
      onClick={openModal}
    >
      <UsergroupAddOutlined />
      Invite Your Team
    </button>
  );

  const mobileButton = (
    <button
      className={`text-white text-lg bg-gradient-to-br from-pink-500 to-blue-700 bg-origin-border bg-clip-padding flex items-center p-3 rounded-full shadow-xl`}
      type="button"
      onClick={openModal}
    >
      <UsergroupAddOutlined />
    </button>
  );

  const minimizedButton = (
    <button
      className="text-white text-lg bg-gradient-to-br from-pink-500 to-blue-700 bg-origin-border bg-clip-padding flex items-center p-3 rounded-full shadow-xl"
      onClick={openModal}
    >
      <UsergroupAddOutlined />
    </button>
  );

  return (
    <>
      {mobile ? mobileButton : isMinimized ? minimizedButton : lgButton}
      {isModalOpen && renderInviteModal()}
    </>
  );
}
